import { DeferredPromise } from '../../utils/DeferredPromise';
import { isBookwise } from '../../utils/environment';
import getServerBaseUrl from '../../utils/getServerBaseUrl.platform';
import makeLogger, { type ReadwiseLogger } from '../../utils/makeLogger';
// eslint-disable-next-line import/no-cycle
import requestWithAuth from '../../utils/requestWithAuth.platformIncludingExtension';
import { retryNetworkRequestWithBackoff } from '../../utils/retryNetworkRequestWithBackoff';
import { KeysOfType } from '../../utils/typescriptUtils';
import { getUniqueDeviceId } from '../../utils/uniqueDeviceId.platform';
import type { ItemStoreConstructorArgs } from './types';

type APIError = {
  code: string;
  id: string;
  meta?: {
    [name: string]: boolean | number | string;
  };
  title: string;
};

export class DeviceNotRegisteredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DeviceNotRegisteredError';
  }
}

interface APIResponseBody<TItem> {
  data: TItem[];
  errors?: APIError[];
  meta?: {
    page: {
      limit: number;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      next_cursor: string;
    };
  };
}

export type OnItemPageReceivedFunction<TItem> =
  (items: TItem[], requestedIds: string[], nextCursor: string | undefined) => Promise<void>;

export class ItemFetcher<TItem, IdPropertyName extends KeysOfType<TItem, string>> {
  readonly endpointPath: string;
  readonly name: string;
  readonly couldServerReturnFewerItemsThanRequested: boolean | undefined;

  private readonly _idPropertyName: IdPropertyName;
  private readonly _logger: ReadwiseLogger;
  private readonly _ongoingFetchByIdsPromises: { [idsConcat: string]: Promise<void>; } = {};

  constructor(options: ItemStoreConstructorArgs) {

    this._logger = makeLogger(`${options.name} item fetcher`, { shouldLogToNewRelic: true });

    this.name = options.name;
    this.endpointPath = options.endpointPath;
    this.couldServerReturnFewerItemsThanRequested = options.couldServerReturnFewerItemsThanRequested;

    // claude eslint: idPropertyName is a string option that must narrow to IdPropertyName (a keyof constraint);
    // the double-cast via `unknown` is the standard TypeScript pattern for this narrowing.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    this._idPropertyName = (options.idPropertyName || 'id') as unknown as IdPropertyName;
  }

  async fetchItemsById({
    idsToFetch,
    onItemPageReceived,
    signal,
    extraQueryParams,
  }: {
    idsToFetch: string[];
    onItemPageReceived?: OnItemPageReceivedFunction<TItem>;
    signal?: AbortSignal;
    extraQueryParams?: URLSearchParams;
  }): Promise<void> {
    this._logger.debug('fetchItemsById', { idsToFetch });
    if (idsToFetch.length === 0) {
      throw new Error('No IDs given');
    }
    const idsConcat = idsToFetch.sort().join(',');
    const ongoingPromise = this._ongoingFetchByIdsPromises[idsConcat];
    if (ongoingPromise) {
      this._logger.debug('ongoing fetchItemsById already happening, bailing', {
        idsConcat,
      });
      return ongoingPromise;
    }
    const promise = new DeferredPromise<void>();
    this._ongoingFetchByIdsPromises[idsConcat] = promise;

    try {
      const extraQueryString = extraQueryParams ? extraQueryParams.toString() : '';
      const extraQueryLength = extraQueryString ? extraQueryString.length + 1 : 0; // +1 for '&'
      const maxUriLength = Math.max(1000, 3500 - extraQueryLength); // empirically derived max length until API request fails
      const safeIdBatches: string[][] = [[]];
      for (const id of idsToFetch) {
        const batchLength = safeIdBatches[0].join('%2C').length;
        if (batchLength + id.length < maxUriLength) {
          safeIdBatches[0].push(id);
        } else {
          safeIdBatches.unshift([id]);
        }
      }
      if (safeIdBatches.length > 1) {
        this._logger.debug('Splitting IDs to load to prevent max URI length from being overflowed', {
          safeIdBatches: safeIdBatches.map((batch) => batch.length),
          maxUriLength,
        });
      }
      for (const idBatch of safeIdBatches) {
        await this.fetchItemsPaginated({
          ids: idBatch,
          onItemPageReceived,
          signal,
          extraQueryParams,
        });
      }
      promise.resolve();
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : String(e);
      const isAbortError = errorMessage.includes('aborted') || e instanceof Error && e.name === 'AbortError';

      if (!isAbortError) {
        this._logger.error('Error while loading by IDs', { e, errorMessage, idsToFetch });
      }

      if (e instanceof Error) {
        promise.reject(e);
      } else {
        promise.reject(new Error(`unknown error: ${e}`));
      }
    } finally {
      delete this._ongoingFetchByIdsPromises[idsConcat];
    }

    return promise;
  }

  public async fetchItemsByCursor({
    cursorToStartAt,
    updatedSince,
    signal,
    onItemPageReceived,
    extraQueryParams,
  }: {
    cursorToStartAt?: string;
    updatedSince?: number;
    signal?: AbortSignal;
    onItemPageReceived?: OnItemPageReceivedFunction<TItem>;
    extraQueryParams?: URLSearchParams;
  }): Promise<void> {
    await this.fetchItemsPaginated({
      cursorToStartAt,
      updatedSince,
      signal,
      onItemPageReceived,
      extraQueryParams,
    });
  }

  private async fetchItemsPaginated({
    ids: idsArgument,
    cursorToStartAt,
    updatedSince,
    onItemPageReceived,
    signal,
    extraQueryParams,
  }: {
    ids?: string[];
    cursorToStartAt?: string;
    updatedSince?: number;
    onItemPageReceived?: OnItemPageReceivedFunction<TItem>;
    signal?: AbortSignal;
    extraQueryParams?: URLSearchParams;
  }): Promise<void> {
    const ids = idsArgument ?? [];
    let nextCursor: string | undefined = cursorToStartAt;

    let totalItemsReturnedCount = 0;
    // Load one page, keep loading more if there's a next_cursor returned
    do {
      const pageResult = await this.fetchPage({
        cursor: nextCursor,
        ids,
        updatedSince,
        signal,
        extraQueryParams,
      });
      const items = pageResult.data;
      totalItemsReturnedCount += items.length;
      nextCursor = pageResult.meta?.page?.next_cursor;

      if (pageResult.errors?.length) {
        this._logger.warn('fetch received some errors', { cursor: nextCursor, ids, pageResult });
        const deviceNotRegisteredError = pageResult.errors.find((e) => e.code === 'DEVICE_NOT_REGISTERED');
        if (deviceNotRegisteredError) {
          throw new DeviceNotRegisteredError(deviceNotRegisteredError.title);
        }
        throw new Error(`fetch received some errors: ${JSON.stringify(pageResult.errors)}`);
      } else if (
        totalItemsReturnedCount < ids.length &&
        !this.couldServerReturnFewerItemsThanRequested &&
        !nextCursor
      ) {
        this._logger.warn(`fetch: server returned fewer items than requested (and no errors)`, {
          ids,
          idsCount: ids.length,
          nextCursor,
          totalItemsReturnedCount,
          itemsReturnedInThisPage: items.length,
          updatedSince,
        });
      }

      this._logger.debug('fetch: Item page received from server', {
        itemIds: items.map((item) => item[this._idPropertyName]),
      });
      await onItemPageReceived?.(items, ids, nextCursor);
    } while (nextCursor);
  }

  private async fetchPage({
    cursor,
    ids = [],
    updatedSince,
    signal,
    extraQueryParams,
  }: {
    cursor?: string;
    ids: string[];
    updatedSince?: number;
    signal?: AbortSignal;
    extraQueryParams?: URLSearchParams;
  }): Promise<APIResponseBody<TItem>> {
    const queryParams = new URLSearchParams();
    if (ids.length) {
      queryParams.append(`filter[${String(this._idPropertyName)}]`, ids.join(','));
    }
    if (updatedSince) {
      queryParams.append('filter[updated_at][gt]', updatedSince.toString());
    }
    if (cursor) {
      queryParams.append('page[cursor]', cursor);
    }
    if (isBookwise) {
      // Some endpoints (e.g. document content) have special behavior for the Bookwise app vs Reader app.
      queryParams.set('bookwise', 'true');
    }

    if (extraQueryParams) {
      for (const [key, value] of extraQueryParams.entries()) {
        queryParams.append(key, value);
      }
    }

    const headers: { [key: string]: string; } = {};
    if (isBookwise) {
      const deviceId = await getUniqueDeviceId();
      headers['Device-Id'] = deviceId;
      queryParams.append('issue-license', '1');
    }

    const startTime = performance.now();
    const apiPath = this.endpointPath.replace(/^\/|\/$/g, '');
    const url = `${getServerBaseUrl()}/reader/api/${apiPath}?${queryParams}`;

    return retryNetworkRequestWithBackoff(async () => {
      const response = await requestWithAuth(url, {
        credentials: 'include',
        method: 'GET',
        mode: 'cors',
        headers,
        signal,
      });

      let body: APIResponseBody<TItem> | undefined;
      try {
        body = await response.json();
      } catch {
        // Ignore now, this is handled later
      }
      this._logger.debug(`Fetching page ${cursor} took ${(performance.now() - startTime).toFixed(3)}ms`);

      if (!body) {
        const error = new Error('fetchPage: failed to parse response body');
        this._logger.error(error.message, {
          cursor,
          ids,
          response,
          url,
        });
        throw error;
      }

      return body;
    }, {
      maxAttempts: 3,
      initialDelay: 1000,
      multiplier: 2,
      maxDelay: 30000,
      signal,
    });
  }

}
